import React, { useEffect, useMemo, useState } from 'react';
import '../form/Form.css';
import './ProcessTemplateEditor.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import Textfield from '../input/Textfield';
import Togglebox from '../input/Togglebox';
import ParticipantGroupPriceMatrix from '../participant-group/ParticipantGroupPriceMatrix';
import TemplateIconField from './TemplateIconField';
import TemplateStepsEditor from './TemplateStepsEditor';
import TemplateMailRulesEditor from './TemplateMailRulesEditor';
import FlatRateRowsEditor from '../flat-rate/FlatRateRowsEditor';
import AddOnRowsEditor, { ADD_ON_ROW_MODE } from '../add-on/AddOnRowsEditor';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useParticipantGroups from '../../hooks/useParticipantGroups';
import { invalidateAddOns } from '../../hooks/useAddOns';
import { COLOR_KEY_LABELS, TEMPLATE_COLOR_KEYS, colorVar, normalizeColorKey } from '../../util/participantGroups';
import { FLAT_RATE_KIND } from '../../util/flatRates';
import {
  COMPLETION_RULE_LABEL, TEMPLATE_RULES, addOnIssue, apiErrorMessage, blankForm, flatRateIssue, formSignature,
  formToRequest, joinFlatRateRows, priceIssue, splitFlatRateRows, stepIssues, templateToForm, usageParts,
} from './templateModel';

const DEFAULT_TEMPLATE_ICON = 'account_tree';

// Tab label with a small marker when the tab holds something to fix.
const tabLabel = (text, flagged) => (flagged
  ? <>{text}<span className="ptTabDot" role="img" aria-label="(Hinweise)" /></>
  : text);

/**
 * Template editor (/processtemplates): tabs Allgemein · Regeln · Schritte · Preise · Zusätze · Pauschalen &
 * Abzüge · Mails · Verwendung. `template` = ProcessTemplateResponse from the list (null = new) — the list
 * response already holds steps, entry prices, add-ons, flat rates/deductions, mail rules and usage, so opening
 * needs no request. Save: POST Validate first (errors block, warnings need a confirmation), then Create/Update;
 * the response goes to onSaved. refData: useTemplateReferenceData() of the page (textblocks, mail slots, group
 * mail rules, add-on catalog — loaded when the tab "Zusätze" opens —, flat-rate catalog + recipient candidates
 * — loaded when the tab "Pauschalen & Abzüge" opens).
 */
function ProcessTemplateEditor({ template, refData, onClose, onSaved }) {
  const { groups, byId, loaded: groupsLoaded } = useParticipantGroups();
  const { isOffline } = useOffline();
  const { openPopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();
  const { textblocks, textblocksFailed, slots, groupMailRules, ensureTextblocks, ensureSlots, ensureGroupMailRules } = refData;
  const { flatRateCatalog, flatRateRecipients, addOnCatalog } = refData;
  const ensureFlatRateCatalog = flatRateCatalog.ensureLoaded;
  const ensureFlatRateRecipients = flatRateRecipients.ensureLoaded;
  const ensureAddOnCatalog = addOnCatalog.ensureLoaded;

  const [form, setForm] = useState(() => (template ? templateToForm(template) : blankForm()));
  const [baseline] = useState(() => formSignature(template ? templateToForm(template) : blankForm()));
  const [tab, setTab] = useState('general');
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [triedSave, setTriedSave] = useState(false);

  const isNew = form.id == null;
  const dirty = formSignature(form) !== baseline;
  const colorKey = normalizeColorKey(form.colorKey);

  // Textblocks are needed by the steps (mail targets) and the mail rules; the slot catalog only by mails.
  useEffect(() => { ensureTextblocks(); }, [ensureTextblocks]);
  useEffect(() => { if (tab === 'mails') ensureSlots(); }, [tab, ensureSlots]);
  // Catalog + recipient candidates only once the tab is opened (once per page visit, see refData).
  useEffect(() => {
    if (tab !== 'flatRates') return;
    ensureFlatRateCatalog();
    ensureFlatRateRecipients();
  }, [tab, ensureFlatRateCatalog, ensureFlatRateRecipients]);
  // Add-on catalog only once the tab is opened (shared list, fetched at most once per page load).
  useEffect(() => { if (tab === 'addOns') ensureAddOnCatalog(); }, [tab, ensureAddOnCatalog]);

  const patch = (changes) => setForm(f => ({ ...f, ...changes }));
  const setSteps = (steps) => setForm(f => ({ ...f, steps }));
  // form.flatRates is one list (Pauschalen, then Abzüge); each area of the tab edits its slice.
  const setFlatRateRows = (rows) => setForm(f => ({
    ...f,
    flatRates: joinFlatRateRows({ ...splitFlatRateRows(f.flatRates), flatRates: rows }),
  }));
  const setDeductionRows = (rows) => setForm(f => ({
    ...f,
    flatRates: joinFlatRateRows({ ...splitFlatRateRows(f.flatRates), deductions: rows }),
  }));
  const setAddOnRows = (rows) => setForm(f => ({ ...f, addOns: rows }));
  const patchPrice = (groupId, changes) => setForm(f => ({
    ...f,
    prices: { ...f.prices, [groupId]: { entry: '', ...f.prices[groupId], ...changes } },
  }));
  const resetPrices = () => setForm(f => ({ ...f, prices: {} }));
  const patchMailRule = (key, changes) => setForm(f => ({
    ...f,
    mailRules: { ...f.mailRules, [key]: { enabled: null, textblockId: null, ...f.mailRules[key], ...changes } },
  }));
  const resetMailScope = (groupId) => setForm(f => {
    const suffix = `|${groupId ?? 'all'}`;
    const next = {};
    Object.keys(f.mailRules).forEach(key => { if (!key.endsWith(suffix)) next[key] = f.mailRules[key]; });
    return { ...f, mailRules: next };
  });

  // ── Hints per tab ──
  const rules = { neverPublic: form.neverPublic, requiresLeader: form.requiresLeader };
  const generalMissing = triedSave && (!form.name.trim() || !form.eventLabel.trim());
  const stepsFlagged = groupsLoaded && form.steps.some(s => stepIssues(s, rules, byId).length > 0);
  const priceError = priceIssue(form, byId);
  const flatRateError = flatRateIssue(form);
  const addOnError = addOnIssue(form, byId);

  // Price rows: active groups + groups (archived) that still carry an override, so it stays visible.
  const priceGroups = useMemo(
    () => groups.filter(g => !g.isArchived || form.prices[g.id] != null),
    [groups, form.prices],
  );
  const hasPriceOverrides = Object.values(form.prices).some(r => (r.entry ?? '') !== '');

  // Steps that contradict "Nie öffentlich" (guest release / newsletter) — shown at that rule.
  const neverPublicConflicts = form.neverPublic
    ? form.steps.filter(s => (s.kind === 'Release' && s.targets.some(t => byId.get(Number(t.participantGroupId))?.isGuest))
      || (s.kind === 'SendMail' && s.targets.some(t => t.targetKind === 'Newsletter')))
    : [];
  const missingLeaderStep = form.requiresLeader && !form.steps.some(s => s.kind === 'AssignLeaders');

  const ruleNote = (key) => {
    if (key === 'linkSignup' && form.linkSignup && !form.neverPublic) {
      return 'Events mit Einladungslink sind üblicherweise nie öffentlich.';
    }
    if (key === 'neverPublic' && neverPublicConflicts.length > 0) {
      const names = neverPublicConflicts.map(s => `„${s.label.trim() || 'ohne Bezeichnung'}“`).join(', ');
      return `Widerspricht ${neverPublicConflicts.length === 1 ? 'dem Schritt' : 'den Schritten'} ${names} – bitte unter „Schritte“ anpassen.`;
    }
    if (key === 'requiresLeader' && missingLeaderStep) {
      return 'Es gibt keinen Schritt „Spieltagleiter“ – unter „Schritte“ hinzufügen.';
    }
    return null;
  };

  // ── Save ──
  const runSave = async () => {
    const request = formToRequest(form);
    setSaving(true);
    setErrors([]);
    startSuccessBarLoading(isNew ? 'Vorlage wird angelegt' : 'Vorlage wird gespeichert');
    try {
      const response = isNew
        ? await api.post('/ProcessTemplate/Create', request)
        : await api.put(`/ProcessTemplate/Update/${form.id}`, request);
      // The add-on catalog's usage counts may have changed — refetched on its next load.
      invalidateAddOns();
      startSuccessBarSuccess(isNew ? 'Vorlage angelegt' : 'Vorlage gespeichert');
      setSaving(false);
      onSaved?.(response.data);
    } catch (err) {
      closeSuccessBar();
      setSaving(false);
      setErrors([apiErrorMessage(err, 'Speichern fehlgeschlagen.')]);
    }
  };

  const handleSave = async () => {
    setTriedSave(true);
    if (priceError) {
      setErrors([priceError]);
      setTab('prices');
      return;
    }
    if (addOnError) {
      setErrors([addOnError]);
      setTab('addOns');
      return;
    }
    if (flatRateError) {
      setErrors([flatRateError]);
      setTab('flatRates');
      return;
    }
    setSaving(true);
    setErrors([]);
    startSuccessBarLoading('Vorlage wird geprüft');
    let result;
    try {
      result = (await api.post('/ProcessTemplate/Validate', formToRequest(form))).data ?? {};
    } catch (err) {
      closeSuccessBar();
      setSaving(false);
      setErrors([apiErrorMessage(err, 'Prüfen fehlgeschlagen.')]);
      return;
    }
    const validationErrors = Array.isArray(result.errors) ? result.errors : [];
    const validationWarnings = Array.isArray(result.warnings) ? result.warnings : [];
    if (validationErrors.length > 0) {
      closeSuccessBar();
      setSaving(false);
      setErrors(validationErrors);
      return;
    }
    if (validationWarnings.length > 0) {
      closeSuccessBar();
      setSaving(false);
      setWarnings(validationWarnings);
      openPopup('templatesavewarnings', form.id ?? 'new');
      return;
    }
    await runSave();
  };

  const requestClose = () => {
    if (dirty) openPopup('templatediscard', form.id ?? 'new');
    else onClose?.();
  };

  // ── Tabs ──
  const tabs = [
    { value: 'general', label: tabLabel('Allgemein', generalMissing), icon: 'badge' },
    { value: 'rules', label: 'Regeln', icon: 'rule' },
    { value: 'steps', label: tabLabel('Schritte', stepsFlagged), icon: 'checklist' },
    { value: 'prices', label: tabLabel('Preise', !!priceError), icon: 'payments' },
    { value: 'addOns', label: tabLabel('Zusätze', !!addOnError), icon: 'inventory_2' },
    { value: 'flatRates', label: tabLabel('Pauschalen & Abzüge', !!flatRateError), icon: 'request_quote' },
    { value: 'mails', label: 'Mails', icon: 'mail' },
    ...(isNew ? [] : [{ value: 'usage', label: 'Verwendung', icon: 'insights' }]),
  ];

  // Arrow keys move between tabs (roving tabindex), like the group editor.
  const handleTabKeyDown = (event, index) => {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = tabs[(index + step + tabs.length) % tabs.length].value;
    setTab(next);
    document.getElementById(`ptEditorTab-${next}`)?.focus();
  };

  const renderGeneral = () => (
    <div className="ptEditorSection">
      <div className="ptEditorGrid">
        <div className="ptEditorField">
          <Textfield
            label="Name"
            icon="badge"
            value={form.name}
            maxLength={100}
            placeholder="z. B. Öffentlicher Spieltag"
            onChange={e => patch({ name: e.target.value })}
          />
          {triedSave && !form.name.trim() && <span className="ptFieldError">Bitte einen Namen angeben.</span>}
        </div>
        <div className="ptEditorField">
          <Textfield
            label="Event-Bezeichnung (Einzahl)"
            icon="label"
            value={form.eventLabel}
            maxLength={50}
            placeholder="z. B. Spieltag"
            onChange={e => patch({ eventLabel: e.target.value })}
          />
          {triedSave && !form.eventLabel.trim()
            ? <span className="ptFieldError">Bitte eine Bezeichnung für die Events angeben.</span>
            : <span className="formFieldHint">So heißt ein einzelnes Event dieser Vorlage – z. B. in Statistik und Tages-Push.</span>}
        </div>
      </div>

      <div className="textFieldWrapper">
        <label className="textFieldLabel" htmlFor="ptDescription">Beschreibung</label>
        <textarea
          id="ptDescription"
          className="ptTextarea"
          rows={3}
          maxLength={500}
          placeholder="Wofür ist diese Vorlage gedacht? (optional)"
          value={form.description}
          onChange={e => patch({ description: e.target.value })}
        />
      </div>

      <div className="ptEditorGrid">
        <div className="ptEditorField">
          <span className="ptFieldCaption" id="ptColorCaption">Farbe</span>
          <div className="ptSwatches" role="radiogroup" aria-labelledby="ptColorCaption">
            {TEMPLATE_COLOR_KEYS.map(key => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={colorKey === key}
                aria-label={COLOR_KEY_LABELS[key]}
                title={COLOR_KEY_LABELS[key]}
                className={`ptSwatch${key === 'neutral' ? ' isNeutral' : ''}${colorKey === key ? ' isActive' : ''}`}
                style={{ '--pt-swatch-color': colorVar(key) }}
                onClick={() => patch({ colorKey: key })}
              />
            ))}
          </div>
          <span className="formFieldHint">{COLOR_KEY_LABELS[colorKey]} – färbt das Icon der Vorlage.</span>
        </div>
        <div className="ptEditorField">
          <TemplateIconField
            label="Icon"
            value={form.icon}
            fallbackIcon={DEFAULT_TEMPLATE_ICON}
            colorKey={colorKey}
            onChange={icon => patch({ icon })}
          />
          <span className="formFieldHint">
            Name eines Material Symbols, z. B. „sports_esports“ –{' '}
            <a className="ptLink" href="https://fonts.google.com/icons?icon.style=Rounded" target="_blank" rel="noopener noreferrer">
              Symbole ansehen
            </a>
          </span>
        </div>
      </div>
    </div>
  );

  const renderRules = () => (
    <div className="ptEditorSection">
      <p className="formFieldHint">
        Regeln werden beim Anlegen eines Prozesses bzw. Events übernommen – bestehende Prozesse und Events behalten ihre Regeln.
      </p>
      <ul className="ptRuleList">
        {TEMPLATE_RULES.map(r => {
          const note = ruleNote(r.key);
          return (
            <li key={r.key} className="ptRuleRow">
              <span className="material-symbols-rounded ptRuleIcon" aria-hidden="true">{r.icon}</span>
              <span className="ptRuleText">
                <span className="ptRuleLabel">{r.label}</span>
                <span className="ptRuleHint">{r.hint}</span>
                {note && (
                  <span className="ptRuleNote">
                    <span className="material-symbols-rounded" aria-hidden="true">warning</span>
                    {note}
                  </span>
                )}
              </span>
              <span className="ptRuleSwitch" title={form[r.key] ? `${r.label}: an` : `${r.label}: aus`}>
                <Togglebox checked={!!form[r.key]} onChange={e => patch({ [r.key]: e.target.checked })} />
              </span>
            </li>
          );
        })}
        <li className="ptRuleRow isReadonly">
          <span className="material-symbols-rounded ptRuleIcon" aria-hidden="true">flag</span>
          <span className="ptRuleText">
            <span className="ptRuleLabel">Abschluss</span>
            <span className="ptRuleHint">{COMPLETION_RULE_LABEL[form.completionRule]}</span>
          </span>
          <span className="ptRuleFixed" title="Weitere Abschlussregeln folgen später">
            <span className="material-symbols-rounded" aria-hidden="true">lock</span>
            Fest
          </span>
        </li>
      </ul>
    </div>
  );

  const renderPrices = () => (
    <div className="ptEditorSection">
      <p className="formFieldHint">
        Eintritt je Gruppe. Leeres Feld = Standardpreis der Gruppe (grau angezeigt). Eingetragene Preise überschreiben
        ihn für Events dieser Vorlage. Preise wirken nur beim Anlegen neuer Events – bestehende Events behalten ihre
        Preise. Preise für Zusätze (z. B. Leihausrüstung) stehen im Tab „Zusätze“.
      </p>
      {!groupsLoaded ? (
        <p className="ptEditorMessage">Gruppen werden geladen …</p>
      ) : (
        <ParticipantGroupPriceMatrix
          groups={priceGroups}
          rows={form.prices}
          onChange={patchPrice}
          inherit
          inheritedValues={g => ({ entry: g.defaultEntryPrice })}
          ariaLabel="Eintritt je Gruppe (leer = Standard der Gruppe)"
        />
      )}
      {priceError && <span className="ptFieldError">{priceError}</span>}
      {hasPriceOverrides && (
        <div className="ptEditorInlineActions">
          <ActionButton icon="restart_alt" label="Alle Preise auf Gruppenstandard" onClick={resetPrices} />
        </div>
      )}
    </div>
  );

  const renderAddOns = () => (
    <div className="ptEditorSection">
      <p className="formFieldHint">
        Anmelde-Zusätze (z. B. Leihausrüstung) werden beim Anlegen eines Prozesses kopiert und gelten dann für alle
        Events des Prozesses. Leere Felder übernehmen die Katalogwerte an der Prozess-Location; entfällt ein Zusatz dort,
        wird er nicht übernommen. „Homepage“ bietet ihn zusätzlich in den Anmeldeformularen und über den persönlichen
        Link an. Im Prozess bleiben die Werte änderbar.
      </p>
      <AddOnRowsEditor
        mode={ADD_ON_ROW_MODE.template}
        rows={form.addOns}
        onChange={setAddOnRows}
        addOns={addOnCatalog.addOns}
        addOnsLoaded={addOnCatalog.loaded}
        addOnsFailed={addOnCatalog.failed}
        groups={groups}
        eventNoun={form.eventLabel.trim() === 'Spieltag' ? 'Spieltag' : 'Event'}
        showErrors={triedSave}
        emptyText="Keine Zusätze – Prozesse dieser Vorlage bieten bei der Anmeldung nichts zusätzlich an."
      />
    </div>
  );

  const renderFlatRates = () => {
    const { flatRates: flatRateRows, deductions: deductionRows } = splitFlatRateRows(form.flatRates);
    return (
      <div className="ptEditorSection">
        <p className="formFieldHint">
          Standard-Pauschalen und -Abzüge werden beim Anlegen eines Prozesses mit dem aktuellen Katalogbetrag der
          Prozess-Location kopiert; entfällt ein Eintrag dort, wird er nicht übernommen. Im Prozess bleiben sie änderbar.
        </p>
        <FlatRateRowsEditor
          title="Pauschalen"
          kind={FLAT_RATE_KIND.flatRate}
          rows={flatRateRows}
          onChange={setFlatRateRows}
          flatRates={flatRateCatalog.flatRates}
          flatRatesLoaded={flatRateCatalog.loaded}
          flatRatesFailed={flatRateCatalog.failed}
          candidates={flatRateRecipients.candidates}
          candidatesById={flatRateRecipients.byId}
          candidatesLoaded={flatRateRecipients.loaded}
          candidatesFailed={flatRateRecipients.failed}
          showErrors={triedSave}
          emptyText="Keine Standard-Pauschalen."
        />
        <FlatRateRowsEditor
          title="Abzüge"
          kind={FLAT_RATE_KIND.deduction}
          rows={deductionRows}
          onChange={setDeductionRows}
          flatRates={flatRateCatalog.flatRates}
          flatRatesLoaded={flatRateCatalog.loaded}
          flatRatesFailed={flatRateCatalog.failed}
          showErrors={triedSave}
          emptyText="Keine Standard-Abzüge."
        />
      </div>
    );
  };

  const renderUsage = () => {
    const u = usageParts(form.usage);
    const tiles = [
      { label: 'Prozesse', value: u.processCount, icon: 'account_tree' },
      { label: 'davon offen', value: u.openProcessCount, icon: 'pending_actions' },
      { label: 'Events', value: u.eventCount, icon: 'event' },
      { label: 'davon kommend', value: u.futureEventCount, icon: 'event_upcoming' },
    ];
    return (
      <div className="ptEditorSection">
        <ul className="ptUsageTiles">
          {tiles.map(t => (
            <li key={t.label} className="ptUsageTile">
              <span className="material-symbols-rounded" aria-hidden="true">{t.icon}</span>
              <span className="ptUsageValue">{t.value}</span>
              <span className="ptUsageLabel">{t.label}</span>
            </li>
          ))}
        </ul>
        <ul className="ptUsageNotes">
          <li>Regeln, Schritte, Preise, Zusätze sowie Pauschalen &amp; Abzüge gelten nur für Prozesse und Events, die nach dem Speichern angelegt werden. Laufende Prozesse behalten ihre Kopie der Schritte, Zusätze sowie Pauschalen &amp; Abzüge.</li>
          <li>Mailregeln wirken sofort – auch bei laufenden Prozessen und bestehenden Events.</li>
          <li>Vorlagen werden nicht gelöscht, sondern archiviert: Archivierte Vorlagen stehen für neue Prozesse nicht mehr zur Wahl.</li>
        </ul>
      </div>
    );
  };

  let body;
  switch (tab) {
    case 'rules': body = renderRules(); break;
    case 'steps':
      body = (
        <TemplateStepsEditor
          form={form}
          onChangeSteps={setSteps}
          groups={groups}
          groupsById={byId}
          groupsLoaded={groupsLoaded}
          textblocks={textblocks}
        />
      );
      break;
    case 'prices': body = renderPrices(); break;
    case 'addOns': body = renderAddOns(); break;
    case 'flatRates': body = renderFlatRates(); break;
    case 'mails':
      body = (
        <TemplateMailRulesEditor
          mailRules={form.mailRules}
          onPatchRule={patchMailRule}
          onResetScope={resetMailScope}
          groups={groups}
          slots={slots}
          textblocks={textblocks}
          groupMailRules={groupMailRules}
          onNeedGroupRules={ensureGroupMailRules}
        />
      );
      break;
    case 'usage': body = renderUsage(); break;
    default: body = renderGeneral();
  }

  const title = form.name.trim() || (isNew ? 'Neue Vorlage' : 'Vorlage');
  const subtitle = [
    isNew ? 'Neue Vorlage' : (form.eventLabel.trim() ? `Events heißen „${form.eventLabel.trim()}“` : null),
    dirty ? 'Ungespeicherte Änderungen' : null,
  ].filter(Boolean).join(' · ');

  return (
    <React.Fragment>
      <div className="ptEditorHeader">
        <button
          type="button"
          className="ptEditorBack"
          onClick={requestClose}
          aria-label="Zurück zu den Prozessvorlagen"
          title="Zurück zu den Prozessvorlagen"
        >
          <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
        </button>
        <span
          className={`material-symbols-rounded ptEditorIcon${colorKey === 'neutral' ? ' isNeutral' : ''}`}
          style={{ '--pt-editor-color': colorVar(colorKey) }}
          aria-hidden="true"
        >
          {form.icon || DEFAULT_TEMPLATE_ICON}
        </span>
        <div className="ptEditorHeaderText">
          <h2 className="ptEditorTitle">{title}</h2>
          {subtitle && <span className="ptEditorSubtitle">{subtitle}</span>}
        </div>
      </div>

      {form.isArchived && (
        <div className="ptEditorBanner" role="note">
          <span className="material-symbols-rounded" aria-hidden="true">archive</span>
          Archiviert – diese Vorlage steht für neue Prozesse und Events nicht zur Wahl.
        </div>
      )}
      {textblocksFailed && (tab === 'steps' || tab === 'mails') && (
        <div className="ptEditorBanner isWarning" role="note">
          <span className="material-symbols-rounded" aria-hidden="true">warning</span>
          Textbausteine konnten nicht geladen werden – die Auswahl ist leer.
        </div>
      )}
      {tab === 'addOns' && addOnCatalog.failed && (
        <div className="ptEditorBanner isWarning" role="note">
          <span className="material-symbols-rounded" aria-hidden="true">warning</span>
          Zusätze konnten nicht geladen werden – die Auswahl ist leer.
        </div>
      )}
      {tab === 'flatRates' && (flatRateCatalog.failed || flatRateRecipients.failed) && (
        <div className="ptEditorBanner isWarning" role="note">
          <span className="material-symbols-rounded" aria-hidden="true">warning</span>
          {flatRateCatalog.failed
            ? 'Pauschalen & Abzüge konnten nicht geladen werden – die Auswahl ist leer.'
            : 'Empfänger konnten nicht geladen werden – es stehen nur die Rollen zur Wahl.'}
        </div>
      )}

      {/* Same underline tabs as the group editor and the other paged admin views (Spieler, Statistiken). */}
      <div className="ptEditorTabs" role="tablist" aria-label="Bereich der Vorlage">
        {tabs.map((t, index) => {
          const selected = t.value === tab;
          return (
            <button
              key={t.value}
              id={`ptEditorTab-${t.value}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls="ptEditorPanel"
              tabIndex={selected ? 0 : -1}
              className={`ptEditorTab${selected ? ' isActive' : ''}`}
              onClick={() => setTab(t.value)}
              onKeyDown={e => handleTabKeyDown(e, index)}
            >
              <span className="material-symbols-rounded ptEditorTabIcon" aria-hidden="true">{t.icon}</span>
              {t.label}
            </button>
          );
        })}
      </div>

      <section className="ptEditorBody" role="tabpanel" id="ptEditorPanel" aria-labelledby={`ptEditorTab-${tab}`}>{body}</section>

      {errors.length > 0 && (
        <div className="newError ptEditorErrors" role="alert">
          <strong>Speichern nicht möglich:</strong>
          <ul>
            {errors.map((text, i) => <li key={i}>{text}</li>)}
          </ul>
        </div>
      )}

      {isOffline && (
        <p className="formOfflineHint">
          <span className="material-symbols-rounded formOfflineHintIcon" aria-hidden="true">cloud_off</span>
          Offline – Speichern ist nicht möglich.
        </p>
      )}
      <div className="ptEditorActions">
        <ActionButton label="Abbrechen" onClick={requestClose} />
        <ActionButton
          icon="save"
          label={isNew ? 'Vorlage anlegen' : 'Speichern'}
          type={2}
          onClick={handleSave}
          disabled={saving || isOffline || (!isNew && !dirty)}
        />
      </div>

      <Popup
        name="templatesavewarnings"
        headerText="Hinweise zur Vorlage"
        confirmText="Trotzdem speichern"
        type="warning"
        onConfirm={runSave}
        withCancel
        wide
      >
        <p>Die Vorlage lässt sich speichern, aber bitte prüfe:</p>
        <ul className="ptPopupList">
          {warnings.map((text, i) => <li key={i}>{text}</li>)}
        </ul>
      </Popup>
      <Popup
        name="templatediscard"
        headerText="Änderungen verwerfen?"
        confirmText="Verwerfen"
        type="confirmaction"
        onConfirm={() => onClose?.()}
        withCancel
      >
        <p>Deine Änderungen an <b>{title}</b> werden nicht gespeichert.</p>
      </Popup>
    </React.Fragment>
  );
}

export default ProcessTemplateEditor;
